import { create } from 'zustand';
import { User } from '@/types/auth';
import { supabase } from '@/lib/supabase/client';

interface UserState {
  users: User[];
  isLoading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  addUser: (user: User) => void;
  updateUser: (userId: string, data: Partial<User>) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  initialize: () => () => void;
}

export const useUserStore = create<UserState>((set, get) => ({
  users: [],
  isLoading: false,
  error: null,

  fetchUsers: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .neq('role', 'worker') // Exclude workers
        .neq('email', 'cybersnow@imatics.com') // Exclude super admin
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ users: data || [] });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to fetch users' });
    } finally {
      set({ isLoading: false });
    }
  },

  addUser: (user: User) => {
    set(state => ({
      users: [user, ...state.users]
    }));
  },

  updateUser: async (userId: string, data: Partial<User>) => {
    try {
      set({ isLoading: true, error: null });
      const { error } = await supabase
        .from('users')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;

      // Update local state immediately
      set(state => ({
        users: state.users.map(user =>
          user.id === userId ? { ...user, ...data } : user
        )
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to update user' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteUser: async (userId: string) => {
    try {
      set({ isLoading: true, error: null });

      // Call Edge Function for admin-level user deletion (service role stays server-side)
      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: { userId },
      });

      if (error) throw new Error(error.message || 'Failed to delete user');
      if (data?.error) throw new Error(data.error);

      // Update local state immediately
      set(state => ({
        users: state.users.filter(user => user.id !== userId)
      }));
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Failed to delete user' });
      throw err;
    } finally {
      set({ isLoading: false });
    }
  },

  initialize: () => {
    // Setup realtime subscription only — initial fetch is handled by the page component
    // on mount, when the auth session is guaranteed to be restored
    const channel = supabase.channel('users_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'users'
        },
        async () => {
          await get().fetchUsers();
        }
      )
      .subscribe();

    // Return cleanup function
    return () => {
      supabase.removeChannel(channel);
    };
  }
}));

// Initialize the store
useUserStore.getState().initialize();